import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';

interface WebSocketOptions {
  url?: string;
  autoConnect?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

interface WebSocketState {
  isConnected: boolean;
  lastMessage: unknown | null;
  error: Error | null;
}

interface OrderEvent {
  type: 'order_created' | 'order_updated' | 'order_status_changed';
  data: {
    id: string;
    status?: string;
    [key: string]: unknown;
  };
}

interface CandidateEvent {
  type: 'candidate_created' | 'candidate_updated' | 'candidate_status_changed' | 'survey_completed';
  data: {
    id: string;
    status?: string;
    [key: string]: unknown;
  };
}

type EventData = OrderEvent | CandidateEvent | { type: string; data: unknown };

const DEFAULT_OPTIONS: WebSocketOptions = {
  url: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
  autoConnect: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 3000,
};

export function useWebSocket(options: WebSocketOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  const [state, setState] = useState<WebSocketState>({
    isConnected: false,
    lastMessage: null,
    error: null,
  });

  // Handle incoming messages and update React Query cache
  const handleMessage = useCallback(
    (event: EventData) => {
      setState((prev) => ({ ...prev, lastMessage: event }));

      // Handle order events
      if (event.type.startsWith('order_')) {
        const orderEvent = event as OrderEvent;
        if (orderEvent.type === 'order_created' || orderEvent.type === 'order_updated') {
          // Invalidate orders query to refetch
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
        if (orderEvent.type === 'order_status_changed') {
          // Update specific order in cache
          queryClient.invalidateQueries({ queryKey: ['orders', orderEvent.data.id] });
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
      }

      // Handle candidate events
      if (event.type.startsWith('candidate_') || event.type === 'survey_completed') {
        const candidateEvent = event as CandidateEvent;
        queryClient.invalidateQueries({ queryKey: ['candidates'] });
        if (candidateEvent.data?.id) {
          queryClient.invalidateQueries({ queryKey: ['candidates', candidateEvent.data.id] });
        }
      }
    },
    [queryClient]
  );

  // Connect to WebSocket
  useEffect(() => {
    if (!opts.autoConnect) return;

    const socket = io(opts.url!, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: opts.reconnectionAttempts,
      reconnectionDelay: opts.reconnectionDelay,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('WebSocket connected');
      setState((prev) => ({ ...prev, isConnected: true, error: null }));
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setState((prev) => ({ ...prev, isConnected: false }));
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setState((prev) => ({ ...prev, error: error as Error, isConnected: false }));
    });

    // Listen for order events
    socket.on('order:created', (data) => handleMessage({ type: 'order_created', data }));
    socket.on('order:updated', (data) => handleMessage({ type: 'order_updated', data }));
    socket.on('order:status', (data) => handleMessage({ type: 'order_status_changed', data }));

    // Listen for candidate events
    socket.on('candidate:created', (data) => handleMessage({ type: 'candidate_created', data }));
    socket.on('candidate:updated', (data) => handleMessage({ type: 'candidate_updated', data }));
    socket.on('candidate:status', (data) => handleMessage({ type: 'candidate_status_changed', data }));
    socket.on('candidate:survey_completed', (data) => handleMessage({ type: 'survey_completed', data }));

    // General message handler
    socket.on('message', handleMessage);

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [opts.url, opts.autoConnect, opts.reconnectionAttempts, opts.reconnectionDelay, handleMessage]);

  // Send a message
  const sendMessage = useCallback((event: string, data: unknown) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    } else {
      console.warn('WebSocket is not connected');
    }
  }, []);

  // Subscribe to a specific channel/room
  const subscribe = useCallback((channel: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe', channel);
    }
  }, []);

  // Unsubscribe from a channel/room
  const unsubscribe = useCallback((channel: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe', channel);
    }
  }, []);

  // Manual connect
  const connect = useCallback(() => {
    if (socketRef.current && !socketRef.current.connected) {
      socketRef.current.connect();
    }
  }, []);

  // Manual disconnect
  const disconnect = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.disconnect();
    }
  }, []);

  return {
    ...state,
    sendMessage,
    subscribe,
    unsubscribe,
    connect,
    disconnect,
    socket: socketRef.current,
  };
}

// Hook for subscribing to order updates
export function useOrderUpdates(orderId?: string) {
  const { subscribe, unsubscribe, lastMessage, isConnected } = useWebSocket();

  useEffect(() => {
    if (orderId && isConnected) {
      subscribe(`order:${orderId}`);
      return () => unsubscribe(`order:${orderId}`);
    }
  }, [orderId, isConnected, subscribe, unsubscribe]);

  return { lastUpdate: lastMessage, isConnected };
}

// Hook for subscribing to candidate updates
export function useCandidateUpdates(candidateId?: string) {
  const { subscribe, unsubscribe, lastMessage, isConnected } = useWebSocket();

  useEffect(() => {
    if (candidateId && isConnected) {
      subscribe(`candidate:${candidateId}`);
      return () => unsubscribe(`candidate:${candidateId}`);
    }
  }, [candidateId, isConnected, subscribe, unsubscribe]);

  return { lastUpdate: lastMessage, isConnected };
}
